/* eslint-disable @typescript-eslint/no-explicit-any */
import type { GridItem, Position, Breakpoint } from './focusOrderManagerUtils';
import type { AlgoliaProductHit } from '../../../types/algolia';
import type { ContentBlockDataSchema } from '../../unmanaged/partials/ContentBlock';
import type { ReactNode } from 'react';

import { useMemo } from 'react';

import {
  buildInjectedContentMap,
  buildOrderedItems,
  getGridColumnsPerRow,
} from './focusOrderManagerUtils';
import { InjectedContentItem } from './InjectedContentItem';
import { useMediaQueryContext } from '../../../utilities/context/dynamic/MediaQueryContext';
import { useProductListContext } from '../../../utilities/context/static/ProductListContext';
import { ContentBlock } from '../../unmanaged/partials/ContentBlock';
import type { Content } from '~/amplienceTypes/schemas/exported/banner-schema';
import { removeLegacyModifierClassNames } from '~/components/unmanaged/styles/utils';
import { DEFAULT_GRID_COLUMNS } from '~/utilities/constants/plpGrid';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { storageAvailable } from '~/utilities/helpers';

export type {
  Position,
  GridItem,
  ProductTileData,
  InjectedContentData,
} from './focusOrderManagerUtils';

type FocusOrderManagerProps = {
  children: (items: GridItem[]) => ReactNode;
  hits: AlgoliaProductHit[];
  renderProductTile: (hit: AlgoliaProductHit, index: number) => ReactNode;
};

/**
 * FocusOrderManager
 *
 * This component ensures that keyboard navigation follows the visual order
 * of items in the grid, not just the DOM order.
 *
 * Reference: https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_grid_layout/Grid_layout_and_accessibility
 * Jira ticket: https://g-star.atlassian.net/browse/FS-3974
 *
 */

export const FocusOrderManager = ({
  children,
  hits,
  renderProductTile,
}: FocusOrderManagerProps) => {
  const { filteredInjectedContent, contentSize = 0 } = useProductListContext();
  const { breakpoint } = useMediaQueryContext();
  const { siteType } = useAppContext();
  const isLocalStorageAvailable = storageAvailable('localStorage');

  const orderedItems = useMemo(() => {
    const productsAmount = hits.length;
    const defaultGridColumns = DEFAULT_GRID_COLUMNS[siteType];
    const desktopGridColumns =
      (isLocalStorageAvailable && Number(localStorage.getItem('plp-columns-desktop'))) ||
      defaultGridColumns.desktop;
    const mobileGridColumns =
      (isLocalStorageAvailable && Number(localStorage.getItem('plp-columns-mobile'))) ||
      defaultGridColumns.mobile;

    const currentGridItemsPerRow = getGridColumnsPerRow(
      breakpoint as Breakpoint,
      desktopGridColumns,
      mobileGridColumns
    );

    const injectedContentMap = buildInjectedContentMap(
      filteredInjectedContent as any,
      currentGridItemsPerRow,
      productsAmount,
      contentSize
    );

    const renderInjectedContent = (
      content: Content,
      position: Position | undefined,
      deliveryId: string
    ) => (
      <InjectedContentItem
        key={deliveryId}
        positionDesktop={position?.desktop}
        positionTablet={position?.tablet}
        positionMobile={position?.mobile}
        spanDesktop={(content as any)?.size?.desktop}
        spanTablet={(content as any)?.size?.tablet}
        spanMobile={(content as any)?.size?.mobile}
      >
        {/* Types generated from amplience for content schema are so messed up, I wasn't capable to make TS happy */}
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <ContentBlock
          // TODO: FS-3437 - PLP Moodbanners remove code for suppressing inline styling
          {...removeLegacyModifierClassNames(content.contentBlock as ContentBlockDataSchema)}
          _meta={content?._meta}
          columnCount={contentSize}
        />
      </InjectedContentItem>
    );

    return buildOrderedItems(hits, injectedContentMap, renderProductTile, renderInjectedContent);
  }, [
    hits,
    filteredInjectedContent,
    contentSize,
    breakpoint,
    renderProductTile,
    isLocalStorageAvailable,
    siteType,
  ]);

  return <>{children(orderedItems)}</>;
};
